import { useEffect, useState } from 'react';
import { BodyText, Button, LayoutBox, Loader } from '@bamboohr/fabric';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import config from '../../config';
import api from '../../services/api';
import { Supplement } from 'src/pages/home/home.types';
import { stripSupplementTitle } from 'src/pages/home/utils';
import { reportError } from 'src/utils/rollbar';
import { useMessageStore } from 'src/store';
import { variantData } from '../messageBanner/variantData';
import CodeBlock from '../codeBlock/CodeBlock';
import './SupplementCard.css';

function supplementStrings(functionId: string) {
	return config.SUPPLEMENTS[functionId] ?? config.DEFAULT_SUPPLEMENT;
}

interface SupplementCardProps {
	supplement: Supplement;
	chatId?: string;
}

const SupplementCard = ({ supplement, chatId }: SupplementCardProps) => {
	const { title, errorText } = supplementStrings(supplement.functionId);
	const { sharedWithCustomer } = supplement;
	const canShare =
		supplement.status === 'done' &&
		typeof sharedWithCustomer === 'boolean' &&
		Boolean(chatId);
	const [isShared, setIsShared] = useState(Boolean(sharedWithCustomer));
	const [isSaving, setIsSaving] = useState(false);
	const showMessage = useMessageStore((state) => state.setMessage);

	useEffect(() => {
		setIsShared(Boolean(sharedWithCustomer));
	}, [sharedWithCustomer]);

	const toggleShared = () => {
		if (!canShare || isSaving) return;
		const next = !isShared;
		setIsSaving(true);
		setIsShared(next);
		api
			.put(`${config.VERSION_ENDPOINT}chat/${chatId}`, {
				function_id: supplement.functionId,
				shared_with_customer: next,
			})
			.then(({ data }) => {
				if (data !== config.SUPPLEMENT_UPDATE_SUCCESS_MESSAGE) {
					throw new Error('The recommendation sharing update was not saved');
				}
			})
			.catch((error) => {
				setIsShared(!next);
				reportError(
					`Supplement ${supplement.functionId}: shared-with-customer was not saved`,
					error,
				);
				const { headerText, subText } = variantData['supplement-share-error'];
				showMessage({
					message: headerText,
					description: subText,
					visible: true,
					type: 'error',
				});
			})
			.finally(() => setIsSaving(false));
	};

	// On the wrapper, so it is mounted before any state resolves into it.
	return (
		<div aria-live="polite">
			{supplement.status === 'loading' ? (
				<LayoutBox marginTop="20px">
					<Loader />
				</LayoutBox>
			) : (
				<div className="supplement-card">
					<div className="supplement-card-header">
						<div className="supplement-card-title">{title}</div>
						{canShare && (
							<span className="supplement-share">
								<Button
									aria-pressed={isShared}
									disabled={isSaving}
									color="secondary"
									onClick={toggleShared}
									size="extra-small"
									startIcon={isShared ? 'check-solid' : 'share-regular'}
									variant="text"
								>
									{isShared ? 'Shared with customer' : 'Mark as shared'}
								</Button>
							</span>
						)}
					</div>
					{supplement.status === 'error' && (
						<BodyText size="small" color="error-strong">
							{errorText}
						</BodyText>
					)}
					{supplement.status === 'abstained' && (
						<BodyText size="small" color="neutral-weak" italic>
							{supplement.text}
						</BodyText>
					)}
					{supplement.status === 'done' && (
						<ReactMarkdown
							className="react-markdown"
							remarkPlugins={[remarkGfm]}
							components={{
								code: CodeBlock,
								a: ({ node, children, ...props }) => (
									<a {...props} target="_blank" rel="noopener noreferrer">
										{children && children.length > 0 ? children : props.href}
									</a>
								),
							}}
						>
							{stripSupplementTitle(supplement.text ?? '', title)}
						</ReactMarkdown>
					)}
				</div>
			)}
		</div>
	);
};

export default SupplementCard;
